import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { Loader, Button, Stepper, Icon } from '@treezor/design-system';

import { clearbulkCardCreationDraft, setBulkCardCreationStep } from '@/store/actions/bulkCardCreations.actions';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useConfirmationDialog } from '@/hooks/useConfirmationModal';
import { BulkName } from '@/features/cards/components/bulkCardCreation/BulkName';
import { BulkOptions } from '@/features/cards/components/bulkCardCreation/BulkOptions';
import { BulkReviewDetails } from '@/features/cards/components/bulkCardCreation/BulkReviewDetails';
import { BulkWalletAndUser } from '@/features/cards/components/bulkCardCreation/BulkWalletAndUser';
import { CardProgram } from '@/features/cards/components/bulkCardCreation/CardProgram';
import { CardsQuantity } from '@/features/cards/components/bulkCardCreation/CardsQuantity';
import { PermsGroup } from '@/features/cards/components/bulkCardCreation/PermsGroup';
import { useRoutepaths } from '@/router/hooks/useRoutePaths';
import { Page } from '@/ui-kit/templates/Page';

export const BulkStepper = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const PathRoute = useRoutepaths();

  const activeStep = useAppSelector((state) => state.bulkCardCreation.currentStep);

  const error = useAppSelector((state) => state.bulkCardCreation.error);
  const isLoading = useAppSelector((state) => state.bulkCardCreation.isLoading);

  const labels = [
    t('pages.administration.bulkCards.stepper.labels.quantity'),
    t('pages.administration.bulkCards.stepper.labels.program'),
    t('pages.administration.bulkCards.stepper.labels.group'),
    t('pages.administration.bulkCards.stepper.labels.walletUser'),
    t('pages.administration.bulkCards.stepper.labels.name'),
    t('pages.administration.bulkCards.stepper.labels.options'),
    t('pages.administration.bulkCards.stepper.labels.create'),
  ];

  const stepContent = [
    <CardsQuantity key="cards-quantity" />,
    <CardProgram key="card-program" />,
    <PermsGroup key="perms-group" />,
    <BulkWalletAndUser key="bulk-wallet-user" />,
    <BulkName key="bulk-name" />,
    <BulkOptions key="bulk-options" />,
    <BulkReviewDetails key="bulk-review" />,
  ];

  const toggleCloseBulkCardCreationDialog = () => CloseBulkCardCreationDialog.toggle();

  const manageConfirmation = () => {
    dispatch(clearbulkCardCreationDraft());
    navigate(PathRoute.app.bulkCards.main);
    toggleCloseBulkCardCreationDialog();
  };

  const CloseBulkCardCreationDialog = useConfirmationDialog({
    title: t('pages.administration.bulkCards.stepper.modal.title'),
    description: t(`pages.administration.bulkCards.stepper.modal.description`),
    confirm: { label: t('pages.modal.sure'), action: manageConfirmation },
  });

  const iconWrapperClassName = 'flex items-center justify-center w-20 h-20 rounded-full bg-opacity-20';
  const titleClassName = 'typo-h5-bold text-secondary text-center max-w-[360px]';

  const bulkCardCreationStepper = (
    <div className="flex flex-col items-center w-full h-full p-5">
      <div className="flex items-start w-full justify-between gap-3">
        <Button
          variant="ghost"
          disabled={activeStep === 0}
          id="bulk-stepper-back-btn"
          icon="arrow_back"
          onClick={() => dispatch(setBulkCardCreationStep(activeStep - 1))}
        >
          {t('pages.administration.bulkCards.stepper.back')}
        </Button>

        <Stepper
          direction="horizontal"
          activeStepIndex={activeStep}
          items={labels.map((step, index) => ({
            id: `step-${index}`,
            title: step,
          }))}
        />

        <Button variant="ghost" id="bulk-stepper-close-btn" icon="close" onClick={toggleCloseBulkCardCreationDialog}>
          {t('pages.administration.bulkCards.stepper.close')}
        </Button>
      </div>
      <div className="flex flex-col items-center w-full justify-center mt-8">{stepContent[activeStep]}</div>
      <CloseBulkCardCreationDialog.ConfirmationDialog />
    </div>
  );

  const displayCreationSucceded = (
    <div className="flex flex-col items-center h-full gap-5 m-auto">
      <div className={twMerge(iconWrapperClassName, 'bg-icon-success')}>
        <Icon name="check_circle" size="xl" className="text-icon-success" />
      </div>

      <h5 className={titleClassName}>{t(`pages.administration.bulkCards.stepper.creation.title`)}</h5>
      <span className="typo-body-regular-small text-helper">
        {t(`pages.administration.bulkCards.stepper.creation.subtitle`)}
      </span>

      <Button
        id="validate-bulk-cards-confirm-creation-btn"
        onClick={() => {
          dispatch(clearbulkCardCreationDraft());
          navigate(PathRoute.app.bulkCards.main);
        }}
      >
        {t(`pages.administration.bulkCards.stepper.creation.confirmation`)}
      </Button>
    </div>
  );

  const displayCreationFailed = (
    <div className="flex flex-col items-center h-full gap-5 m-auto">
      <div className={twMerge(iconWrapperClassName, 'bg-icon-success')}>
        <Icon name="check_circle" size="xl" className="text-icon-success" />
      </div>

      <h5 className={titleClassName}>{t(`pages.administration.bulkCards.stepper.creationFailed.title`)}</h5>
      <div className="flex gap-2">
        <p className="typo-body-regular-small text-secondary text-center">
          {t('pages.administration.bulkCards.stepper.creationFailed.error')}
          {': '}
          {(error as { errors?: Array<{ message: string }> })?.errors?.map((el, index) => (
            <span key={`error-${index}`} className="text-error">
              {el.message}
            </span>
          ))}
        </p>
      </div>
      <Button
        id="validate-bulk-cards-creation-failed-btn"
        color="danger"
        onClick={() => dispatch(setBulkCardCreationStep(0))}
      >
        {t('pages.administration.bulkCards.stepper.creationFailed.goBack')}
      </Button>
    </div>
  );

  return (
    <Page>
      {activeStep === stepContent.length ? (
        isLoading ? (
          <Loader />
        ) : (error as { errors?: Array<unknown> })?.errors?.length ? (
          displayCreationFailed
        ) : (
          displayCreationSucceded
        )
      ) : (
        bulkCardCreationStepper
      )}
    </Page>
  );
};
